import { match, P } from "ts-pattern";
import { z } from "zod";
import { safeParse } from "../../lib/zod";
import { ControlDataTypeKey } from "../../common";
import {
  ControlDefinition,
  serialize
} from "../definition";
import { DefaultControlInstance } from "../instance";
class Definition extends ControlDefinition {
  constructor(config, version) {
    super(config);
    this.version = version;
  }
  static v1DataType = "text-area::v1";
  static dataSignature = {
    v1: { [ControlDataTypeKey]: this.v1DataType }
  };
  static type = "makeswift::controls::text-area";
  static get schema() {
    const version = z.literal(1).optional();
    const versionedData = z.object({
      [ControlDataTypeKey]: z.literal(this.v1DataType),
      value: z.string()
    });
    const schemas = (value, data) => {
      const type = z.literal(this.type);
      const config = z.object({
        label: z.string().optional(),
        defaultValue: value,
        rows: z.number().optional()
      });
      const definition = z.object({
        type,
        config,
        version
      });
      return {
        type,
        data,
        value,
        resolvedValue: value,
        config,
        definition,
        versionedData,
        version
      };
    };
    return {
      version,
      relaxed: schemas(
        z.string().optional(),
        z.union([z.string(), versionedData, z.undefined()])
      ),
      strict: schemas(z.string(), z.union([z.string(), versionedData]))
    };
  }
  static deserialize(data) {
    if (data.type !== Definition.type) {
      throw new Error(
        `TextArea: expected type ${Definition.type}, got ${data.type}`
      );
    }
    const { version, config } = Definition.schema.relaxed.definition.parse(data);
    return new TextAreaDefinition(config, version);
  }
  get controlType() {
    return Definition.type;
  }
  get schema() {
    return Definition.schema.relaxed;
  }
  get dataSchema() {
    return (this.config.defaultValue === void 0 ? Definition.schema.relaxed : Definition.schema.strict).data;
  }
  safeParse(data) {
    return safeParse(this.dataSchema, data);
  }
  fromData(data) {
    const inputSchema = this.dataSchema.optional();
    return match(data).with(Definition.dataSignature.v1, ({ value }) => value).otherwise((val) => val);
  }
  toData(value) {
    return match({ version: this.version, value }).with({ version: 1, value: P.string }, ({ value: value2 }) => ({
      ...Definition.dataSignature.v1,
      value: value2
    })).with({ version: 1, value: void 0 }, () => void 0).with({ version: void 0 }, ({ value: value2 }) => value2).otherwise(() => value);
  }
  copyData(data, _context) {
    return data;
  }
  getTranslatableData(data) {
    return data;
  }
  mergeTranslatedData(data, translatedData, _context) {
    if (translatedData == null)
      return data;
    return translatedData;
  }
  resolveValue(data) {
    return {
      name: Definition.type,
      readStable: () => this.fromData(data) ?? this.config.defaultValue,
      subscribe: () => () => {
      },
      triggerResolve: async () => {
      }
    };
  }
  createInstance(sendMessage) {
    return new DefaultControlInstance(sendMessage);
  }
  serialize() {
    return serialize(this.config, {
      type: Definition.type,
      version: this.version
    });
  }
}
class TextAreaDefinition extends Definition {
}
function TextArea(config) {
  return new TextAreaDefinition(config ?? {}, 1);
}
export {
  TextArea,
  TextAreaDefinition
};
//# sourceMappingURL=text-area.js.map